import { CELLS_REACH, COLLISION_CELLS_PER_TILE, type EntityInstance, type JsonSchema, type JsonValue, type ValidationIssue } from "daedalus-core";
import { CONTENT_FILE_ID_PATTERN } from "../content/line.ts";
import { SLUG_PATTERN } from "../content/slug.ts";
import { spawnLevelCap } from "../creatures/spawn.ts";
import { NPC_TEMPERAMENTS, isNpcTemperament, type NpcTemperament } from "./policy.ts";

/**
 * The server-only `npc` marker (ADR-136 decision 2.1): the template and dialogue it names, how it rests and moves,
 * its temperament and level, and how long it waits to return. The marker's id is the NPC's definition id. Pure: the
 * editor's Issues list, the compile and the map server read it the same way. Checks that need the world (a standable
 * cell, a look's walk strip, a template that exists) belong to the compile.
 */

/**
 * The name of the list an NPC's template is chosen from in the editor: the committed NPC templates.
 */
export const NPC_TEMPLATE_LOOKUP = "npcs";

/**
 * The name of the list an NPC's dialogue is chosen from in the editor: the committed dialogue files.
 */
export const NPC_DIALOGUE_LOOKUP = "dialogues";

/**
 * The directions an NPC may rest facing.
 */
export const NPC_FACINGS = ["down", "left", "right", "up"] as const;
export type NpcFacing = (typeof NPC_FACINGS)[number];

/**
 * How an NPC moves: not at all, within a painted area, along a path of cells, or anywhere within `roamTiles`.
 */
export const NPC_MOVEMENTS = ["stand", "area", "path", "roam"] as const;
export type NpcMovement = (typeof NPC_MOVEMENTS)[number];

/**
 * What a path does at its last point: `loop` returns to the first point, `back` walks the path in reverse.
 */
export const NPC_PATH_ENDS = ["loop", "back"] as const;
export type NpcPathEnds = (typeof NPC_PATH_ENDS)[number];

/**
 * The fewest points a path holds.
 */
export const MIN_PATH_POINTS = 2;

/**
 * The most points a path holds.
 */
export const MAX_PATH_POINTS = 64;

/**
 * The least `roamTiles`.
 */
export const MIN_ROAM_TILES = 4;

/**
 * The most `roamTiles`.
 */
export const MAX_ROAM_TILES = 64;

/**
 * The shortest respawn, 10 seconds.
 */
export const MIN_NPC_RESPAWN_TICKS = 200;

/**
 * The longest respawn, an hour.
 */
export const MAX_NPC_RESPAWN_TICKS = 72_000;

/**
 * The most cells a painted area holds.
 */
export const MAX_AREA_CELLS = 4096;

/**
 * How far from its marker, in cells on each axis, an area or path cell may lie.
 */
export const MAX_CELL_OFFSET = 256;

/**
 * How far, in cells, a path point may lie past the marker's tile on each side: the reach the editor lets a point be
 * placed within (`CELLS_REACH` tiles).
 */
export const PATH_REACH_CELLS = CELLS_REACH * COLLISION_CELLS_PER_TILE;

const TEMPLATE_ID = SLUG_PATTERN;
const TICKS_PER_SECOND = 20;
const DEFAULT_ROAM_TILES = 24;
const DEFAULT_RESPAWN_TICKS = 6000;

/**
 * The marker's fields as the editor's inspector draws them. The `path` field is an ordered list of cells placed on
 * the map, closed back to its first point while `pathEnds` is `loop`.
 */
export const NPC_SCHEMA: JsonSchema = {
  type: "object",
  properties: {
    template: { type: "string", title: "Template", lookup: NPC_TEMPLATE_LOOKUP },
    dialogue: { type: "string", title: "Dialogue", lookup: NPC_DIALOGUE_LOOKUP },
    facing: { type: "string", title: "Facing", enum: [...NPC_FACINGS], default: "down" },
    movement: { type: "string", title: "Movement", enum: [...NPC_MOVEMENTS], default: "stand" },
    area: { type: "cells", title: "Area" },
    path: { type: "path", title: "Path", maxItems: MAX_PATH_POINTS, closeWhen: { field: "pathEnds", equals: "loop" } },
    pathEnds: { type: "string", title: "Path ends", enum: [...NPC_PATH_ENDS], default: "back" },
    roamTiles: { type: "integer", title: "Roam tiles", default: DEFAULT_ROAM_TILES, minimum: MIN_ROAM_TILES },
    temperament: { type: "string", title: "Temperament", enum: [...NPC_TEMPERAMENTS] },
    level: { type: "integer", title: "Level", minimum: 1 },
    respawn: { type: "integer", title: "Respawn", default: DEFAULT_RESPAWN_TICKS, minimum: MIN_NPC_RESPAWN_TICKS, unit: { symbol: "s", per: TICKS_PER_SECOND } },
  },
  required: ["template", "facing", "movement"],
};

/**
 * A cell as painted: `[column, rowsUp]` from the bottom-left of the marker's tile.
 */
export type NpcCell = readonly [number, number];

/**
 * An NPC marker's fields with every default filled. `dialogue`, `temperament` and `level` are absent when the marker
 * leaves them to the template.
 */
export interface NpcFields {
  readonly template: string;
  readonly dialogue?: string;
  readonly facing: NpcFacing;
  readonly movement: NpcMovement;
  readonly area: readonly NpcCell[];
  readonly path: readonly NpcCell[];
  readonly pathEnds: NpcPathEnds;
  readonly roamTiles: number;
  readonly temperament?: NpcTemperament;
  readonly level?: number;
  /** Ticks until a fallen NPC returns; ignored for a passive one. */
  readonly respawn: number;
}

/**
 * The verdict on one marker's fields; each error is the sentence after the marker's name.
 */
export type NpcFieldsVerdict = { readonly ok: true; readonly npc: NpcFields } | { readonly ok: false; readonly errors: readonly string[] };

/**
 * Whether a value is an integer within a range.
 *
 * @param value The value.
 * @param min Least allowed.
 * @param max Most allowed.
 * @returns True when it is.
 */
function integerIn(value: JsonValue | undefined, min: number, max: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;
}

/**
 * Whether a field is left empty: absent or null.
 *
 * @param value The field.
 * @returns True when empty.
 */
function empty(value: JsonValue | undefined): value is null | undefined {
  return value === undefined || value === null;
}

/**
 * The cells a value holds, each a pair of integers, or null when it is not a list of them.
 *
 * @param value The value.
 * @returns The cells in the order given.
 */
function cellsOf(value: JsonValue | undefined): NpcCell[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const cells: NpcCell[] = [];
  for (const pair of value) {
    if (!Array.isArray(pair) || pair.length !== 2 || !Number.isInteger(pair[0]) || !Number.isInteger(pair[1])) {
      return null;
    }
    cells.push([pair[0] as number, pair[1] as number]);
  }
  return cells;
}

/**
 * Whether a cell lies farther than `MAX_CELL_OFFSET` from the marker on either axis.
 *
 * @param cell The cell.
 * @returns True when out of reach.
 */
function outOfReach(cell: NpcCell): boolean {
  return Math.abs(cell[0]) > MAX_CELL_OFFSET || Math.abs(cell[1]) > MAX_CELL_OFFSET;
}

/**
 * Whether a path cell lies outside the reach the editor places points in: `PATH_REACH_CELLS` past the marker's tile
 * on every side.
 *
 * @param cell The cell.
 * @returns True when out of reach.
 */
function outOfPathReach(cell: NpcCell): boolean {
  const last = COLLISION_CELLS_PER_TILE - 1 + PATH_REACH_CELLS;
  return cell[0] < -PATH_REACH_CELLS || cell[0] > last || cell[1] < -PATH_REACH_CELLS || cell[1] > last;
}

/**
 * The first cell a path holds twice.
 *
 * @param path The cells.
 * @returns The repeated cell, or null when every cell is held once.
 */
function repeatedCell(path: readonly NpcCell[]): NpcCell | null {
  const seen = new Set<string>();
  for (const cell of path) {
    const key = `${cell[0]},${cell[1]}`;
    if (seen.has(key)) {
      return cell;
    }
    seen.add(key);
  }
  return null;
}

/**
 * Reads one choice field: empty gives the default, a member of the list is kept, anything else is an error.
 *
 * @param value The field.
 * @param choices The allowed words.
 * @param fallback The word for an empty field.
 * @param refuse Builds the error sentence from the refused value.
 * @param errors Where an error is added.
 * @returns The word.
 */
function choice<T extends string>(value: JsonValue | undefined, choices: readonly T[], fallback: T, refuse: (shown: string) => string, errors: string[]): T {
  if (empty(value)) {
    return fallback;
  }
  if (typeof value !== "string" || !(choices as readonly string[]).includes(value)) {
    errors.push(refuse(String(value)));
    return fallback;
  }
  return value as T;
}

/**
 * Reads and checks an NPC marker's fields: a template, facing, movement, pathEnds, roamTiles, optional temperament and
 * level, a respawn, and the cells the chosen movement needs (an area of at least one cell, a path of 2 to 64 points).
 *
 * @param fields The marker's fields.
 * @param levelCap The most a level may be, the creature table's last row by default.
 * @returns The fields with defaults filled, or every error in the schema's order.
 */
export function readNpcFields(fields: { readonly [key: string]: JsonValue }, levelCap: number = spawnLevelCap()): NpcFieldsVerdict {
  const errors: string[] = [];
  const template = fields["template"];
  if (typeof template !== "string" || template === "") {
    errors.push("names no template; choose an NPC template");
  } else if (!TEMPLATE_ID.test(template)) {
    errors.push(`names the template ${template}; use lower-case letters, digits and -`);
  }
  const dialogue = fields["dialogue"];
  if (!empty(dialogue) && dialogue !== "" && (typeof dialogue !== "string" || !CONTENT_FILE_ID_PATTERN.test(dialogue))) {
    errors.push(`names the dialogue ${String(dialogue)}; use lower-case letters, digits, _ and -`);
  }
  const facing = choice(fields["facing"], NPC_FACINGS, "down", (shown) => `faces ${shown}; use down, left, right or up`, errors);
  const movement = choice(fields["movement"], NPC_MOVEMENTS, "stand", (shown) => `moves by ${shown}; use stand, area, path or roam`, errors);
  const pathEnds = choice(fields["pathEnds"], NPC_PATH_ENDS, "back", (shown) => `ends its path by ${shown}; use loop or back`, errors);
  const roamTiles = fields["roamTiles"];
  if (!empty(roamTiles) && !integerIn(roamTiles, MIN_ROAM_TILES, MAX_ROAM_TILES)) {
    errors.push(`roams ${String(roamTiles)} tiles; keep it from ${MIN_ROAM_TILES} to ${MAX_ROAM_TILES}`);
  }
  const temperament = fields["temperament"];
  if (!empty(temperament) && !isNpcTemperament(temperament)) {
    errors.push(`has the temperament ${String(temperament)}; use ${NPC_TEMPERAMENTS.slice(0, -1).join(", ")} or ${NPC_TEMPERAMENTS[NPC_TEMPERAMENTS.length - 1] as string}`);
  }
  const level = fields["level"];
  if (!empty(level) && !integerIn(level, 1, levelCap)) {
    errors.push(`has a level of ${String(level)}; use a whole number from 1 to ${levelCap}`);
  }
  const respawn = fields["respawn"];
  if (!empty(respawn) && !integerIn(respawn, MIN_NPC_RESPAWN_TICKS, MAX_NPC_RESPAWN_TICKS)) {
    errors.push(`respawns after ${String(respawn)} ticks; set its respawn from ${MIN_NPC_RESPAWN_TICKS / TICKS_PER_SECOND} to ${MAX_NPC_RESPAWN_TICKS / TICKS_PER_SECOND} seconds (${MIN_NPC_RESPAWN_TICKS} to ${MAX_NPC_RESPAWN_TICKS} ticks)`);
  }
  const area = empty(fields["area"]) ? [] : cellsOf(fields["area"]);
  if (area === null) {
    errors.push("has an area that is not a list of [column, rowsUp] pairs of whole numbers");
  } else if (area.length > MAX_AREA_CELLS) {
    errors.push(`has an area of ${area.length} cells; keep it to ${MAX_AREA_CELLS}`);
  } else if (area.some(outOfReach)) {
    errors.push(`has an area cell outside ${MAX_CELL_OFFSET} of its marker; keep every cell within ${MAX_CELL_OFFSET} on each axis`);
  } else if (movement === "area" && area.length === 0) {
    errors.push("moves within an area but has none; paint at least one cell");
  }
  const path = empty(fields["path"]) ? [] : cellsOf(fields["path"]);
  if (path === null) {
    errors.push("has a path that is not a list of [column, rowsUp] pairs of whole numbers");
  } else if (path.length > MAX_AREA_CELLS) {
    errors.push(`has a path of ${path.length} cells; keep it to ${MAX_AREA_CELLS}`);
  } else if (path.some(outOfPathReach)) {
    errors.push(`has a path cell outside the ${PATH_REACH_CELLS} cells of reach around its marker; keep every point within it`);
  } else if (repeatedCell(path) !== null) {
    const again = repeatedCell(path) as NpcCell;
    errors.push(`has a path that holds cell [${again[0]}, ${again[1]}] twice; give each point its own cell`);
  } else if (movement === "path" && (path.length < MIN_PATH_POINTS || path.length > MAX_PATH_POINTS)) {
    errors.push(`walks a path of ${path.length} points; give it ${MIN_PATH_POINTS} to ${MAX_PATH_POINTS}`);
  }
  if (errors.length > 0) {
    return { ok: false, errors };
  }
  return {
    ok: true,
    npc: {
      template: template as string,
      ...(empty(dialogue) || dialogue === "" ? {} : { dialogue: dialogue as string }),
      facing,
      movement,
      area: area as NpcCell[],
      path: path as NpcCell[],
      pathEnds,
      roamTiles: empty(roamTiles) ? DEFAULT_ROAM_TILES : (roamTiles as number),
      ...(empty(temperament) ? {} : { temperament: temperament as NpcTemperament }),
      ...(empty(level) ? {} : { level: level as number }),
      respawn: empty(respawn) ? DEFAULT_RESPAWN_TICKS : (respawn as number),
    },
  };
}

/**
 * The Issues an NPC marker raises in the editor and at a compile, one per bad field, each naming the marker.
 *
 * @param mapId The map.
 * @param layerId The marker's layer.
 * @param entity The marker.
 * @returns The issues.
 */
export function npcIssues(mapId: string, layerId: string, entity: EntityInstance): ValidationIssue[] {
  const verdict = readNpcFields(entity.fields);
  return verdict.ok ? [] : verdict.errors.map((message) => ({ mapId, kind: "npc.field", layerId, entityId: entity.id, message: `npc ${entity.id} ${message}` }));
}
